import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  GPSDRIVE_API_URL,
  getGpsDriveAccessToken,
  gpsDrivePost,
  refreshGpsDriveSession,
} from "@/lib/api/gpsdrive-client";
import { onLocalWrite, type LiveSyncTopic } from "@/lib/live-sync";

// Atualização ao vivo de veículos/usuários (pedido 2026-10-03). Duas
// pontas:
//  - gravação feita NESTA aba (lib/live-sync.ts#reportWrite): recarrega as
//    listas daqui e avisa o backend (POST /live-sync/notify);
//  - aviso de OUTRA tela da empresa (GET /live-sync/stream, SSE): recarrega
//    as listas daqui.
//
// "Recarregar" é invalidateQueries — busca de novo em segundo plano e troca
// só o que mudou, sem F5, sem "carregando", sem perder página/filtro/rolagem.
// Exigência do pedido: nunca atrapalhar quem está trabalhando — com um
// popup aberto ou digitando num campo, o recarregamento espera até a
// pessoa fechar/sair do campo.

const TOPIC_QUERY_KEYS: Record<LiveSyncTopic, readonly (readonly unknown[])[]> = {
  vehicles: [
    ["vehicles"],
    ["company-fleet"],
    ["vehicle-metadata"],
    ["vehicle-metadata-summaries"],
    ["vehicle-images"],
    ["vehicle-reseller-company-map"],
    ["vehicle-users"],
    ["vehicle-trash"],
    ["device-permissions"],
  ],
  users: [
    ["users"],
    ["users-summary"],
    ["linkable-users"],
    ["gpsdrive-user-identity"],
    ["user-avatar"],
    ["sub-users-by-creator"],
    ["sub-users-mine"],
    ["sub-users-company-traccar-ids"],
    ["user-deletions"],
    ["device-permissions"],
    ["vehicle-users"],
  ],
};

const ALL_TOPICS: LiveSyncTopic[] = ["vehicles", "users"];
const CHECK_BUSY_MS = 1_000;
// O vínculo de veículo novo pros outros funcionários (grants-sync) roda no
// backend DEPOIS da resposta do cadastro — uma segunda busca pega o caso em
// que a primeira chegou antes do vínculo.
const SECOND_PASS_MS = 4_000;
const NOTIFY_DEBOUNCE_MS = 300;

function isUserBusy(): boolean {
  if (typeof document === "undefined") return false;
  if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return true;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (el as HTMLInputElement).type;
    // Campo de busca da lista: atualizar por trás não atrapalha.
    return type !== "search" && type !== "checkbox" && type !== "radio" && type !== "button";
  }
  return false;
}

function invalidateTopics(queryClient: QueryClient, topics: Iterable<LiveSyncTopic>): void {
  const seen = new Set<string>();
  for (const topic of topics) {
    for (const key of TOPIC_QUERY_KEYS[topic]) {
      const id = JSON.stringify(key);
      if (seen.has(id)) continue;
      seen.add(id);
      void queryClient.invalidateQueries({ queryKey: key });
    }
  }
}

function randomClientId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function useLiveSync(enabled: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !GPSDRIVE_API_URL) return;

    const clientId = randomClientId();
    let stopped = false;
    const pending = new Set<LiveSyncTopic>();
    let busyTimer: ReturnType<typeof setTimeout> | null = null;
    const secondPassTimers = new Set<ReturnType<typeof setTimeout>>();

    // Segunda busca de veículos (ver SECOND_PASS_MS) — separada de `pending`
    // pra não agendar uma terceira, quarta...
    const secondPass = new Set<LiveSyncTopic>();

    const flush = () => {
      busyTimer = null;
      if (stopped || (pending.size === 0 && secondPass.size === 0)) return;
      if (isUserBusy()) {
        busyTimer = setTimeout(flush, CHECK_BUSY_MS);
        return;
      }
      const topics = [...pending];
      pending.clear();
      invalidateTopics(queryClient, new Set([...topics, ...secondPass]));
      secondPass.clear();
      if (topics.includes("vehicles")) {
        const t = setTimeout(() => {
          secondPassTimers.delete(t);
          secondPass.add("vehicles");
          schedule();
        }, SECOND_PASS_MS);
        secondPassTimers.add(t);
      }
    };

    const schedule = (delay = 0) => {
      if (busyTimer || stopped) return;
      busyTimer = setTimeout(flush, delay);
    };

    // --- Gravação desta aba: atualiza aqui e avisa as outras -------------
    const toNotify = new Set<LiveSyncTopic>();
    let notifyTimer: ReturnType<typeof setTimeout> | null = null;
    const sendNotify = () => {
      notifyTimer = null;
      const topics = [...toNotify];
      toNotify.clear();
      if (topics.length === 0) return;
      void gpsDrivePost("/api/v1/live-sync/notify", { topics, clientId }).catch(() => {});
    };
    const unsubscribeLocal = onLocalWrite((topics) => {
      for (const t of topics) {
        pending.add(t);
        toNotify.add(t);
      }
      // Pequena espera: o cadastro costuma ser 2-3 chamadas em sequência
      // (device → permissão → metadata); um único aviso no fim.
      if (notifyTimer) clearTimeout(notifyTimer);
      notifyTimer = setTimeout(sendNotify, NOTIFY_DEBOUNCE_MS);
      schedule(NOTIFY_DEBOUNCE_MS);
    });

    // --- Avisos das outras telas (SSE via fetch: precisa do Bearer) ------
    let controller: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 5_000;
    let connectedBefore = false;

    const handleFrame = (frame: string) => {
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data:")) continue;
        try {
          const msg = JSON.parse(line.slice(5).trim()) as { topics?: string[]; clientId?: string };
          if (msg.clientId === clientId) continue; // eco da própria aba
          for (const t of msg.topics ?? []) {
            if ((ALL_TOPICS as string[]).includes(t)) pending.add(t as LiveSyncTopic);
          }
          schedule();
        } catch {
          // frame malformado: ignora
        }
      }
    };

    const connect = async () => {
      reconnectTimer = null;
      if (stopped) return;
      const token = getGpsDriveAccessToken();
      if (!token) {
        reconnectTimer = setTimeout(connect, retryDelay);
        return;
      }
      controller = new AbortController();
      try {
        const res = await fetch(`${GPSDRIVE_API_URL}/api/v1/live-sync/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
          credentials: "include",
          signal: controller.signal,
        });
        if (res.status === 401) {
          await refreshGpsDriveSession().catch(() => null);
          throw new Error("401");
        }
        if (!res.ok || !res.body) throw new Error(String(res.status));

        // Reconectou depois de uma queda: pode ter perdido avisos no meio.
        if (connectedBefore) {
          for (const t of ALL_TOPICS) pending.add(t);
          schedule();
        }
        connectedBefore = true;
        retryDelay = 5_000;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buffer.indexOf("\n\n")) !== -1) {
            handleFrame(buffer.slice(0, idx));
            buffer = buffer.slice(idx + 2);
          }
        }
      } catch {
        // queda de rede/servidor reiniciando — reconecta abaixo
      }
      if (stopped) return;
      reconnectTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 60_000);
    };

    void connect();

    return () => {
      stopped = true;
      unsubscribeLocal();
      controller?.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (busyTimer) clearTimeout(busyTimer);
      if (notifyTimer) clearTimeout(notifyTimer);
      for (const t of secondPassTimers) clearTimeout(t);
    };
  }, [enabled, queryClient]);
}
